import { http } from "./http";

/** Cross-customer admin/manager number management. */

export interface AdminNumberListItem {
  dnisId: number;
  dnis: string;
  dnisPresentation: string;
  description: string;
  numberTypeName: string;
  countryName: string;
  customerId: number | null;
  customerName: string;
  scriptName: string;
  active: boolean;
}

export interface PagedNumbers {
  items: AdminNumberListItem[];
  totalCount: number;
  skip: number;
  take: number;
}

export interface AdminNumberDetail {
  dnisId: number;
  dnis: string;
  dnisExtra: string;
  dnisPresentation: string;
  numberTypeId: number;
  numberTypeName: string;
  providerId: number | null;
  providerName: string;
  countryId: number | null;
  countryName: string;
  managerId: number;
  managerName: string;
  customerId: number | null;
  customerName: string;
  description: string;
  scriptId: number;
  scriptName: string;
  userTariffPerCall: number | null;
  userTariffPerMinute: number | null;
  validFromUtc: string | null;
  validTillUtc: string | null;
  active: boolean;
  preferredSwitchId: number | null;
  countryCode: string;
  areaCode: string;
  ringTypeId: number | null;
  ringTypeName: string;
}

export interface SaveNumber {
  providerId: number;
  dnis: string;
  dnisPresentation: string;
  dnisExtra: string;
  numberTypeId: number;
  scriptId: number;
  description: string;
  userTariffPerCall: number | null;
  userTariffPerMinute: number | null;
  validFromUtc: string;
  validTillUtc: string;
  active: boolean;
  countryCode: string;
  areaCode: string;
  preferredSwitchId: number | null;
  ringTypeId: number | null;
}

export interface NumberAssignment {
  customerId: number;
  customerName: string;
  validFromUtc: string;
  validTillUtc: string;
}

export interface NumberVariable {
  variableId: number;
  variableName: string;
  variableValue: string;
}

export interface Lookup {
  id: number;
  name: string;
}

// ---- Number import wizard ----

/** One pasted number after server-side prefix resolution. */
export interface ResolvedNumber {
  number: string;
  /** A country prefix matched (numbers without one cannot be provisioned). */
  valid: boolean;
  /** A Dnis row (active or not) already covers this number right now. */
  exists: boolean;
  /** dnisId of that current row (when exists). */
  existingDnisId: number | null;
  countryId: number | null;
  countryCode: string;
  /** Country code + area code, e.g. "3110" (Dnis.AreaCode convention). */
  areaCode: string;
  countryName: string;
  areaName: string;
  /** "Country, City" / "Country, National" – default DNIS description. */
  description: string;
}

export interface PricingNumberItem {
  id: number;
  customerId: number | null;
  customerName: string;
  setupFee: number;
  monthlyFee: number;
  description: string;
  validFromUtc: string;
  validTillUtc: string;
}

export interface PricingTrafficItem {
  id: number;
  customerId: number | null;
  customerName: string;
  /** null = any origin, "00" = fixed, "01" = mobile */
  cpc: string | null;
  cpcName: string;
  unit: string;
  rate: number;
  description: string;
  validFromUtc: string;
  validTillUtc: string;
}

export interface PricingTerminationItem {
  id: number;
  customerId: number | null;
  customerName: string;
  terminationPricelistId: number;
  terminationPricelistName: string;
  validFromUtc: string;
  validTillUtc: string;
}

export interface NumberPricing {
  number: PricingNumberItem[];
  traffic: PricingTrafficItem[];
  termination: PricingTerminationItem[];
}

export interface ImportNumberBody {
  number: string;
  description: string;
  providerId: number;
  numberTypeId: number;
  scriptId: number;
  userTariffPerCall: number | null;
  userTariffPerMinute: number | null;
  validFromUtc: string;
  validTillUtc: string;
  active: boolean;
  customerId: number | null;
  pricing: {
    number: { setupFee: number; monthlyFee: number; description: string } | null;
    traffic: { cpc: string | null; unit: string; rate: number; description: string; eom: number | null }[];
    terminationPricelistId: number | null;
  } | null;
  /** Set to replace that current DNIS (active or not): it is stopped at getutcdate() and a new DNIS is created. */
  existingDnisId: number | null;
}

export interface ImportNumberResult {
  dnisId: number;
  number: string;
  /** true when an existing DNIS was stopped and replaced by this new one. */
  replaced: boolean;
  replacedDnisId: number | null;
  assigned: boolean;
  pricingItemsCreated: number;
}

const base = "/api/admin/numbers";

export const adminNumbersApi = {
  list: (params: {
    customerId?: number | null;
    search?: string;
    exactSearch?: boolean;
    skip: number;
    take: number;
  }) => {
    const qs = new URLSearchParams();
    if (params.customerId != null) qs.set("customerId", String(params.customerId));
    if (params.search) qs.set("search", params.search);
    if (params.exactSearch) qs.set("exactSearch", "true");
    qs.set("skip", String(params.skip));
    qs.set("take", String(params.take));
    return http<PagedNumbers>(`${base}?${qs.toString()}`);
  },

  get: (dnisId: number) => http<AdminNumberDetail>(`${base}/${dnisId}`),

  create: (body: SaveNumber) =>
    http<{ dnisId: number }>(base, { method: "POST", body: JSON.stringify(body) }),

  update: (dnisId: number, body: SaveNumber) =>
    http<AdminNumberDetail>(`${base}/${dnisId}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  assignments: (dnisId: number) =>
    http<NumberAssignment[]>(`${base}/${dnisId}/assignments`),

  assign: (
    dnisId: number,
    body: { customerId: number; validFromUtc: string; validTillUtc: string },
  ) =>
    http<NumberAssignment[]>(`${base}/${dnisId}/assignments`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  end: (dnisId: number, validTillUtc: string) =>
    http<{ ok: boolean }>(`${base}/${dnisId}/end`, {
      method: "POST",
      body: JSON.stringify({ validTillUtc }),
    }),

  variables: (dnisId: number) =>
    http<NumberVariable[]>(`${base}/${dnisId}/variables`),

  upsertVariable: (
    dnisId: number,
    body: { variableId: number; variableName: string; variableValue: string },
  ) =>
    http<NumberVariable[]>(`${base}/${dnisId}/variables`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  deleteVariable: (dnisId: number, variableId: number) =>
    http<NumberVariable[]>(`${base}/${dnisId}/variables/${variableId}`, {
      method: "DELETE",
    }),

  numberTypes: () => http<Lookup[]>(`${base}/lookups/number-types`),
  scripts: () => http<Lookup[]>(`${base}/lookups/scripts`),
  providers: () => http<Lookup[]>(`${base}/lookups/providers`),
  terminationPricelists: () => http<Lookup[]>(`${base}/lookups/termination-pricelists`),

  // ---- Number import wizard ----
  resolve: (numbers: string[]) =>
    http<ResolvedNumber[]>(`${base}/resolve`, {
      method: "POST",
      body: JSON.stringify({ numbers }),
    }),
  pricing: (dnisId: number) => http<NumberPricing>(`${base}/${dnisId}/pricing`),
  importOne: (body: ImportNumberBody) =>
    http<ImportNumberResult>(`${base}/import`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
};
